import { Mutation, Query } from '@apollo/client/react/components';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { CKButton } from 'clearkit';
import { uniq } from 'lodash';

import MultiPicklist from '~/components/MultiPicklist';
import { trackCSVExported } from '~/lib/segmentAnalytics';

import styles from './CsvExportDialog.style';
import csvExportQuery from './csvExportQuery.graphql';
import exportCsvMutation from './mutation.graphql';

class CsvExportDialog extends React.Component {
  state = {
    columns: undefined,
    step: 'columns',
  };

  columnObjects = (allColumns, stringColumns) => {
    return stringColumns.map((stringColumn) => {
      const fullColumn = allColumns.find(
        (fullColumn) => fullColumn.value === stringColumn,
      );
      return {
        ...fullColumn,
      };
    });
  };

  handleCompleted = (segment, columns) => {
    trackCSVExported(segment, uniq(columns.map((column) => column.category)));
    this.setState({ step: 'confirmation' });
  };

  renderColumnSelector = (value, allColumns) => {
    const { classes, onClose, searchQuery, segment = {}, type } = this.props;
    const { columns = value } = this.state;
    const segmentId = segment.id;

    const emptyWarning =
      "You can't export an empty CSV. Add some attributes from the list to the left.";

    return (
      <>
        <DialogTitle disableTypography>
          Configure the CSV file to export
        </DialogTitle>
        <DialogContent className={classes.dialogContentColumns}>
          By default Clearbit includes a standard set of data attributes in the
          CSV export for this audience. You can customize the attributes that
          are included and specify your own below.
          <MultiPicklist
            hubspotComingSoon
            allColumns={allColumns}
            emptyWarning={emptyWarning}
            value={value}
            onChange={(columns) => {
              this.setState({ columns });
            }}
            type={type}
          />
        </DialogContent>
        <DialogActions classes={{ root: classes.dialogActionsColumns }}>
          <CKButton variant="secondary" onClick={onClose}>
            Dismiss
          </CKButton>
          <Mutation
            onCompleted={() => this.handleCompleted(segment, columns)}
            mutation={exportCsvMutation}
            variables={{
              input: {
                columns,
                searchQuery,
                type,
                ...(segmentId && { segmentId }),
              },
            }}>
            {(exportCsv, { _loading }) => (
              <CKButton
                isDisabled={columns.length === 0}
                variant="bold"
                variantColor="blue"
                onClick={exportCsv}>
                Begin export
              </CKButton>
            )}
          </Mutation>
        </DialogActions>
      </>
    );
  };

  renderConfirmation = (email) => {
    const { classes, onClose } = this.props;

    return (
      <>
        <DialogTitle disableTypography>
          Your CSV download is being prepared
        </DialogTitle>

        <DialogContent classes={{ root: classes.dialogContentRoot }}>
          We&apos;ll email you at the address below when your CSV is ready for
          download.
          <p>{email}</p>
        </DialogContent>
        <DialogActions classes={{ root: classes.dialogActionsRoot }}>
          <CKButton variant="secondary" onClick={onClose}>
            Dismiss
          </CKButton>
        </DialogActions>
      </>
    );
  };

  render() {
    const { classes, open, segment = {}, type } = this.props;
    const { step } = this.state;
    const segmentId = segment.id;

    const variables = {
      attributeType: type,
      columnType: type,
      ...(segmentId && { segmentId }),
    };

    return (
      <Query key="csvExport" query={csvExportQuery} variables={variables}>
        {({ data = {}, loading, error }) => {
          if (error || loading) return null;

          const {
            columns,
            attributes,
            viewer: {
              user: { email },
            },
          } = data;
          const columnObjects = this.columnObjects(attributes, columns);

          return (
            <Dialog
              classes={{ paper: classes.dialogPaper }}
              maxWidth="md"
              open={open}
              onExited={() => this.setState({ step: 'columns' })}>
              {step == 'columns' &&
                this.renderColumnSelector(columnObjects, attributes)}
              {step == 'confirmation' && this.renderConfirmation(email)}
            </Dialog>
          );
        }}
      </Query>
    );
  }
}

CsvExportDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  segment: PropTypes.object,
  type: PropTypes.oneOf(['Company', 'Person']).isRequired,
};

export default withStyles(styles)(CsvExportDialog);
